import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import Swal from 'sweetalert2';
import { postApiCall } from 'api/apiService';
import { REFUND_STATUS_AGENT } from 'constant/app.config';

export default function InitiateDepositRefund({
  open,
  onClose,
  profileDetails,
  actionBuyerRefund,
  actionRefundBuyerId,
  getTableData,
  setActionBuyerId,
  setActionBuyer,
  depositDetailsData,
  setActionBuyerRefund,
  isTable,
  buyerId,
  isFromBuyerRegister,
  getDetailsData,
  tableData,
  setInitiateDepositRefund,
}) {
  const [refundmethod, setRefundMethod] = useState('full');
  const [refundAmount, setRefundAmount] = useState('');
  const [method, setMethod] = useState('bankTransfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState(null);
  const [errors, setErrors] = React.useState({});
  const [selectedData, setSelectedData] = useState(null); // to hold full item

  const validate = () => {
    const newErrors = {};

    if (!refundmethod) {
      newErrors.refundmethod = 'Refund Type is required';
    }

    // Check refundAmount for string or number safely
    if (
      refundAmount === undefined ||
      refundAmount === null ||
      (typeof refundAmount === 'string' && refundAmount.trim() === '') ||
      (typeof refundAmount !== 'string' && isNaN(refundAmount))
    ) {
      newErrors.refundAmount = 'Amount is required';
    }

    if (refundmethod === 'partial') {
      if (!selectedItems || selectedItems.length === 0) {
        newErrors.selectedItems = 'Please select a refund amount option';
      }
    }

    if (!method) {
      newErrors.method = 'Method is required';
    }

    if (!reference || reference.trim() === '') {
      newErrors.reference = 'Reference is required';
    }

    if (!notes || notes.trim() === '') {
      newErrors.notes = 'Notes are required';
    }

    setErrors(newErrors);

    // Return true if no errors, false otherwise
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validate();

    if (isValid) {
      RefundPostCall();
    } else {
      console.log('Validation failed, API call blocked.');
    }
  };

  const partialRefund = isTable
    ? actionBuyerRefund?.amountList
    : depositDetailsData?.depositDetails?.amountList;

  const propertyId = profileDetails?.PropertyDetails?.property?.propertyId;

  const [loading, setLoading] = useState(false);

  const refundAmounts = parseInt(
    (
      actionBuyerRefund?.formattedPaidAmount ||
      depositDetailsData?.depositDetails?.formattedPaidAmount
    )
      ?.replace(/[$,]/g, '')
      .split('.')[0],
    10
  );

  const RefundPostCall = () => {
    setLoading(true);

    const parsedAmount =
      refundmethod === 'partial'
        ? parseInt(
            (selectedData?.transferredAmounts || '0')?.replace(/[$,]/g, ''),
            10
          )
        : parseInt((refundAmount || '0')?.replace(/[$,]/g, ''), 10);

    const inputData = {
      buyerId: isTable ? actionRefundBuyerId : buyerId,
      propertyId: propertyId,
      refundAmount: parsedAmount,
      method: method,
      reference: reference,
      notes: notes,
    };

    // ✅ Add `refundmethod: "partialRefunded"` only when refundmethod is "partial" or "custom"
    if (refundmethod === 'partial' || refundmethod === 'custom') {
      inputData.refundmethod = 'Partial Refunded';
    } else if (refundmethod === 'full') {
      inputData.refundmethod = 'Full Refunded';
    }

    postApiCall(REFUND_STATUS_AGENT, inputData)
      .then((res) => {
        if (isTable) {
          setActionBuyerId('');
          setActionBuyer('');
          setActionBuyerRefund('');
        }

        if (res?.data?.status === 1) {
          if (isTable) {
            getTableData();
          } else {
            getDetailsData();
            if (!isFromBuyerRegister) {
              getTableData();
            }
          }

          Swal.fire({
            title: res?.data?.heading,
            text: res?.data?.message,
            icon: 'success',
            confirmButtonText: 'Close',
          }).then(() => {
            onClose();
          });
        } else {
          Swal.fire({
            title: res?.data?.heading,
            text: res?.data?.message,
            icon: 'error',
            confirmButtonText: 'Close',
          });
        }
      })
      .catch(() => {
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong.',
          icon: 'error',
          confirmButtonText: 'Close',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (actionBuyerRefund || depositDetailsData?.depositDetails) {
      setRefundMethod('full');
      setRefundAmount(
        actionBuyerRefund.formattedPaidAmount ||
          depositDetailsData?.depositDetails?.formattedPaidAmount
      );
    }
  }, []);

  // Update refundAmount if refundMethod changes and is full or custom
  useEffect(() => {
    if (refundmethod === 'full');
    {
      setRefundAmount(
        actionBuyerRefund?.formattedPaidAmount ||
          depositDetailsData?.depositDetails?.formattedPaidAmount
      );
    }
    // For custom refund, you probably want user input, so don't overwrite here
  }, []);

  const handleCancel = () => {
    // setMethod("");
    setRefundMethod('full');
    setRefundAmount('');
    setReference('');
    setNotes('');
    setErrors({});
    onClose(); // finally close the dialog
  };

  const matchedDeposit = tableData?.depositDetails?.find(
    (item) => String(item.buyerId) === String(buyerId)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 3, sm: 5 },
          fontFamily: 'Poppins',
          maxWidth: '499px',
          width: '100%',
          margin: 'auto',
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 400,
            fontFamily: 'Poppins',
            color: '#1E1E1E',
            fontSize: '24px',
          }}
        >
          Initiate Deposit Refund
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Typography
          sx={{ fontFamily: 'Poppins', color: '#666666', fontSize: '12px' }}
        >
          You're about to process a refund for a deposit payment made by buyer
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontSize: '12px', color: '#666666' }}
          >
            {' '}
            {tableData?.depositDetails
              ?.map((row) => row.buyerName)
              .filter(Boolean)
              .join(', ')}
          </Typography>
          .
        </Typography>

        <br />
        <br />

        <Typography
          sx={{ fontFamily: 'Poppins', color: '#666666', fontSize: '12px' }}
        >
          Please select the type of refund and enter the refund amount
          accordingly. Ensure your notes clearly reflect the reason for the
          refund and which portion (if any) of the deposit is being returned.
        </Typography>

        <Grid
          container
          rowSpacing={{ xs: 3, sm: 6 }}
          columnSpacing={3}
          padding="20px"
          alignItems="center"
        >
          {[
            {
              label: 'Refund Type',
              input: (
                <FormControl fullWidth size="small">
                  <Select
                    value={refundmethod}
                    onChange={(e) => setRefundMethod(e.target.value)}
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 300,
                      fontSize: '16px',
                      color: '#666666',
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          fontFamily: 'Poppins',
                          fontWeight: 300,
                          fontSize: '16px',
                          color: '#666666',
                        },
                      },
                    }}
                  >
                    <MenuItem value="full">Full Refund</MenuItem>
                    <MenuItem value="partial">Partial Refund</MenuItem>
                    <MenuItem value="custom">Custom Refund</MenuItem>
                  </Select>
                  {!!errors.refundmethod && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.refundmethod}
                    </Typography>
                  )}
                </FormControl>
              ),
            },
            // ...(refundmethod !== "partial"
            //   ? [
            //       {
            //         label: "Amount",
            //         input: (
            //           <TextField
            //             fullWidth
            //             size="small"
            //             value={refundAmount}
            //             onChange={(e) => {
            //               if (refundmethod === "custom") {
            //                 const rawValue = e.target.value.replace(/,/g, '');
            //                 const number = parseFloat(rawValue);
            //                 if (!isNaN(number)) {
            //                   const formatted = number.toLocaleString("en-US");
            //                   setRefundAmount(formatted);
            //                 } else {
            //                   setRefundAmount('');
            //                 }
            //               }
            //             }}
            //             placeholder="$80,000"
            //             InputProps={{
            //               readOnly: refundmethod === "full",
            //               sx: {
            //                 fontFamily: "Poppins",
            //                 fontWeight: 300,
            //                 fontSize: "16px",
            //                 color: "#666666",
            //               },
            //             }}
            //             error={!!errors.refundAmount}
            //           />
            //         ),
            //       },
            //     ]
            //   : []),

            ...(refundmethod !== 'partial'
              ? [
                  {
                    label: 'Amount',
                    input: (
                      <TextField
                        fullWidth
                        size="small"
                        value={
                          refundmethod === 'custom'
                            ? refundAmount
                              ? typeof refundAmount === 'string'
                                ? refundAmount
                                : '$' +
                                  Number(refundAmount).toLocaleString('en-US')
                              : '$'
                            : actionBuyerRefund?.formattedPaidAmount ||
                              depositDetailsData?.depositDetails?.offerPrice
                        }
                        onChange={(e) => {
                          if (refundmethod === 'custom') {
                            const rawValue = e?.target?.value?.replace(
                              /[^0-9.]/g,
                              ''
                            ); // Remove $, commas, etc.
                            const number = parseFloat(rawValue);
                            if (!isNaN(number)) {
                              const formatted =
                                '$' + number.toLocaleString('en-US');
                              setRefundAmount(formatted);
                            } else {
                              setRefundAmount('$');
                            }
                          }
                        }}
                        placeholder="$80,000"
                        InputProps={{
                          readOnly: refundmethod === 'full',
                          sx: {
                            fontFamily: 'Poppins',
                            fontWeight: 300,
                            fontSize: '16px',
                            color: '#666666',
                          },
                        }}
                        error={!!errors.refundAmount}
                      />
                    ),
                  },
                ]
              : []),

            //             ...(refundmethod === "partial"
            //               ? [
            //                   {
            //                     label: "Refund Amount",
            //                     input: (
            //                       <Grid container direction="column" spacing={2} sx={{ mb: -2 }}>
            //                       {/* First row wrapped in Grid item */}
            //                       <Grid item>
            //                         <Grid container spacing={2} alignItems="center">
            //                           <Grid item>
            //                             <input
            //                               type="checkbox"
            //                               checked={selectedItems === "amount1"}
            //                               onChange={() =>
            //                                 setSelectedItems(selectedItems === "amount1" ? null : "amount1")
            //                               }
            //                               disabled={selectedItems && selectedItems !== "amount1"}
            //                               style={{
            //                                 accentColor: "#A55FFB",
            //                                 cursor: selectedItems && selectedItems !== "amount1" ? "not-allowed" : "pointer",
            //                                 width: "16px",
            //                                 height: "16px",
            //                               }}
            //                             />
            //                           </Grid>
            //                           <Grid item>
            //                             <Typography
            //                               sx={{
            //                                 fontFamily: "Poppins",
            //                                 fontSize: "16px",
            //                                 fontWeight: 300,
            //                                 color: selectedItems && selectedItems !== "amount1" ? "#ccc" : "#000",
            //                               }}
            //                             >
            //                             {actionBuyerRefund?.}
            //                             </Typography>
            //                           </Grid>
            //                           <Grid item>
            //                             <Typography
            //                               sx={{
            //                                 fontFamily: "Poppins",
            //                                 fontSize: "16px",
            //                                 fontWeight: 300,
            //                                 color: selectedItems && selectedItems !== "amount1" ? "#ccc" : "#000",
            //                               }}
            //                             >
            //                               14 Sep 2025
            //                             </Typography>
            //                           </Grid>
            //                         </Grid>
            //                       </Grid>

            //                       {/* Second row wrapped in Grid item */}
            //                       <Grid item>
            //                         <Grid container spacing={2} alignItems="center">
            //                           <Grid item>
            //                             <input
            //                               type="checkbox"
            //                               checked={selectedItems === "amount2"}
            //                               onChange={() =>
            //                                 setSelectedItems(selectedItems === "amount2" ? null : "amount2")
            //                               }
            //                               disabled={selectedItems && selectedItems !== "amount2"}
            //                               style={{
            //                                 accentColor: "#A55FFB",
            //                                 cursor: selectedItems && selectedItems !== "amount2" ? "not-allowed" : "pointer",
            //                                 width: "16px",
            //                                 height: "16px",
            //                               }}
            //                             />
            //                           </Grid>
            //                           <Grid item>
            //                             <Typography
            //                               sx={{
            //                                 fontFamily: "Poppins",
            //                                 fontSize: "16px",
            //                                 fontWeight: 300,
            //                                 color: selectedItems && selectedItems !== "amount2" ? "#ccc" : "#000",
            //                               }}
            //                             >
            //                               $8,000
            //                             </Typography>
            //                           </Grid>
            //                           <Grid item>
            //                             <Typography
            //                               sx={{
            //                                 fontFamily: "Poppins",
            //                                 fontSize: "16px",
            //                                 fontWeight: 300,
            //                                 color: selectedItems && selectedItems !== "amount2" ? "#ccc" : "#000",
            //                               }}
            //                             >
            //                               14 Sep 2025
            //                             </Typography>
            //                           </Grid>
            //                         </Grid>
            //                       </Grid>
            //                       {errors.selectedItems && (
            //   <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            //     {errors.selectedItems}
            //   </Typography>
            // )}
            //                     </Grid>

            //                     ),
            //                   },
            //                 ]
            //               : []),

            ...(refundmethod === 'partial'
              ? [
                  {
                    label: 'Refund Amount',
                    input: (
                      <Grid
                        container
                        direction="column"
                        spacing={2}
                        sx={{ mb: -2 }}
                      >
                        {partialRefund?.map((item, index) => {
                          const itemKey = `amount${index + 1}`;
                          const isChecked = selectedItems === itemKey;

                          return (
                            <Grid item key={itemKey}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {
                                      if (isChecked) {
                                        setSelectedItems(null);
                                        setSelectedData(null);
                                      } else {
                                        setSelectedItems(itemKey);
                                        setSelectedData(item); // ✅ Save actual value
                                      }
                                    }}
                                    disabled={
                                      selectedItems && selectedItems !== itemKey
                                    }
                                    style={{
                                      accentColor: '#A55FFB',
                                      cursor:
                                        selectedItems &&
                                        selectedItems !== itemKey
                                          ? 'not-allowed'
                                          : 'pointer',
                                      width: '16px',
                                      height: '16px',
                                    }}
                                  />
                                </Grid>

                                <Grid item>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Poppins',
                                      fontSize: '16px',
                                      fontWeight: 300,
                                      color:
                                        selectedItems &&
                                        selectedItems !== itemKey
                                          ? '#ccc'
                                          : '#000',
                                    }}
                                  >
                                    ${item.transferredAmounts}
                                  </Typography>
                                </Grid>

                                <Grid item>
                                  <Typography
                                    sx={{
                                      fontFamily: 'Poppins',
                                      fontSize: '16px',
                                      fontWeight: 300,
                                      color:
                                        selectedItems &&
                                        selectedItems !== itemKey
                                          ? '#ccc'
                                          : '#000',
                                    }}
                                  >
                                    {item.lastUpdated}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ),
                  },
                ]
              : []),

            // ...(refundmethod === "partial"
            // ? [
            //     {
            //       label: "Refund Amount",
            //       input: (
            //         <Grid container direction="column" spacing={2} sx={{ mb: -2 }}>
            //           {tableData?.depositDetails?.map((row, idx) => (
            //             <Grid item key={row.binderId || idx}>
            //               <Grid container spacing={2} alignItems="center">
            //                 <Grid item>
            //                   <input
            //                     type="checkbox"
            //                     checked={selectedItems === `amount${idx}`}
            //                     onChange={() =>
            //                       setSelectedItems(
            //                         selectedItems === `amount${idx}` ? null : `amount${idx}`
            //                       )
            //                     }
            //                     disabled={selectedItems && selectedItems !== `amount${idx}`}
            //                     style={{
            //                       accentColor: "#A55FFB",
            //                       cursor:
            //                         selectedItems && selectedItems !== `amount${idx}`
            //                           ? "not-allowed"
            //                           : "pointer",
            //                       width: "16px",
            //                       height: "16px",
            //                     }}
            //                   />
            //                 </Grid>
            //                 <Grid item>
            //                   <Typography
            //                     sx={{
            //                       fontFamily: "Poppins",
            //                       fontSize: "16px",
            //                       fontWeight: 300,
            //                       color:
            //                         selectedItems && selectedItems !== `amount${idx}`
            //                           ? "#ccc"
            //                           : "#000",
            //                     }}
            //                   >
            //                     {row.formattedPaidAmount || "$0"}
            //                   </Typography>
            //                 </Grid>
            //                 <Grid item>
            //                   <Typography
            //                     sx={{
            //                       fontFamily: "Poppins",
            //                       fontSize: "16px",
            //                       fontWeight: 300,
            //                       color:
            //                         selectedItems && selectedItems !== `amount${idx}`
            //                           ? "#ccc"
            //                           : "#000",
            //                     }}
            //                   >
            //                     {row.requestDate
            //   ? new Date(row.requestDate).toLocaleDateString("en-GB", {
            //       day: "2-digit",
            //       month: "short",
            //       year: "numeric",
            //     }) // e.g., "14 Sep 2025"
            //   : "-"}
            //                   </Typography>
            //                 </Grid>
            //               </Grid>
            //             </Grid>
            //           ))}

            //           {errors.selectedItems && (
            //             <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            //               {errors.selectedItems}
            //             </Typography>
            //           )}
            //         </Grid>
            //       ),
            //     },
            //   ]
            // : [])

            {
              label: 'Method',
              input: (
                <FormControl fullWidth size="small">
                  <Select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 300,
                      fontSize: '16px',
                      color: '#666666',
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          fontFamily: 'Poppins',
                          fontWeight: 300,
                          fontSize: '16px',
                          color: '#666666',
                        },
                      },
                    }}
                  >
                    <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
                    <MenuItem value="creditCard">Credit Card</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                  </Select>
                  {!!errors.method && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.method}
                    </Typography>
                  )}
                </FormControl>
              ),
            },
            {
              label: 'Reference',
              input: (
                <TextField
                  fullWidth
                  size="small"
                  value={reference}
                  onChange={(e) => {
                    const value = e.target.value;
                    setReference(value);
                    if (errors.reference && value.trim() !== '') {
                      setErrors((prev) => ({ ...prev, reference: '' }));
                    }
                  }}
                  placeholder="Reference sent with transfer"
                  error={!!errors.reference}
                  helperText={errors.reference}
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 300,
                    fontSize: '16px',
                    color: '#666666',
                    '& .MuiInputBase-input::placeholder': {
                      fontFamily: 'Poppins',
                      fontWeight: 300,
                      fontSize: '16px',
                      color: '#666666',
                      opacity: 1,
                    },
                  }}
                />
              ),
            },

            {
              label: 'Notes',
              input: (
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  value={notes}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNotes(value);
                    if (errors.notes && value.trim() !== '') {
                      setErrors((prev) => ({ ...prev, notes: '' }));
                    }
                  }}
                  placeholder="Add any relevant payment notes"
                  error={!!errors.notes}
                  helperText={errors.notes}
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 300,
                    fontSize: '16px',
                    color: '#666666',
                    '& .MuiInputBase-input::placeholder': {
                      fontFamily: 'Poppins',
                      fontWeight: 300,
                      fontSize: '16px',
                      color: '#666666',
                      opacity: 1,
                    },
                  }}
                />
              ),
            },
          ].map((item, index) => (
            <React.Fragment container key={index}>
              <Grid item xs={12} sm={3}>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    color: '#666666',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {item.input}
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 4, p: 0, mb: { xs: 4, sm: 0 } }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '100%',
            justifyContent: {
              xs: 'center',
              sm: 'flex-end',
            },
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              textTransform: 'none',
              minWidth: 120,
              borderRadius: 2,
              fontFamily: 'Poppins',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={loading || (refundmethod === 'partial' && !selectedItems)}
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              minWidth: 120,
              borderRadius: 2,
              fontFamily: 'Poppins',
              position: 'relative',
            }}
          >
            {loading ? (
              <CircularProgress
                size={20}
                sx={{
                  color: '#fff', // or another color to match the button
                }}
              />
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
